import { TMarketIdentifiers } from '@/core/constants/event_types/base_mable_event';
import { MarketDataHandler } from '@/core/data_handlers/market';
import { PageDataHandler } from '@/core/data_handlers/page_data';
import { getMarketDataFromUIObjectShopify } from '@/shared/__ui_helper';
import { Logger } from '@/shared/logger/logger';

const SHOPIFY_MARKET_DATA_KEY = 'wtag_shp_mkt';

type TShopifyMarketData = {
  handle: string;
  id: string;
  region: string;
};

function getPageLocale(): string {
  return navigator?.language || '';
}

function getMarketDataFromLocalStorage(): TMarketIdentifiers | undefined {
  const shopifyData_str = localStorage.getItem(SHOPIFY_MARKET_DATA_KEY);
  if (!shopifyData_str) return undefined;
  try {
    const shopifyMarketdata = JSON.parse(shopifyData_str) as TShopifyMarketData;
    const marketIdentifiers: TMarketIdentifiers = {
      do: '',
      lo: '',
      mh: shopifyMarketdata?.handle || '',
      mid: shopifyMarketdata?.id || '',
      re: shopifyMarketdata?.region || '',
    };
    return marketIdentifiers;
  } catch (error) {
    Logger.error('Error parsing shopify market data', error);
    return undefined;
  }
}

/**
 * Get the market data from the local storage and the UI object
 * @returns The market data
 *
 * Priority order:
 * Local storage data overrides UI data since the UI object may not always
 * be updated correctly during page transitions or dynamic updates
 */
function getShopifyMarketData(): TMarketIdentifiers {
  const marketDataFromLocalStorage = getMarketDataFromLocalStorage();
  const marketDataFromUI = getMarketDataFromUIObjectShopify();
  const pageData = PageDataHandler.get();
  return {
    ...marketDataFromUI,
    ...marketDataFromLocalStorage,
    do: pageData?.dl || '',
    lo: getPageLocale(),
  };
}

/**
 * ** DEPENDS ON MarketDataHandler **
 * Updates the market data based on Shopify's market information stored in local storage.
 * This function handles three scenarios:
 *
 * 1. If no Shopify market data exists (undefined), the function returns without updates
 * 2. If Shopify market data contains a market ID (mid), it updates the market data directly
 * 3. If no market ID exists, it attempts to find a matching market and:
 *    - Updates the market ID (mid) and handle (mh)
 *    - Sets region (re) if not already defined, using the first region from the matched market
 *    - Sets locale (lo) if not already defined, using the locale from the matched market
 *
 * The function uses the MarketDataHandler to perform the actual updates to the market data.
 *
 * @example
 * // Call the function to sync Shopify market data with the application's market data
 * updateShopifyMarketData();
 *
 * @see {@link MarketDataHandler} for the underlying market data management
 * @see {@link getShopifyMarketData} for the source of Shopify market data
 */
export function updateShopifyMarketData() {
  // const currentMarketData = MarketDataHandler.getMarketData();
  const shopifyMarketData = getShopifyMarketData();

  // TODO: get is calculated from the market data
  // Market ID from shopify is actually available, so just update the whole object!
  if (shopifyMarketData?.mid && shopifyMarketData?.mid !== '') {
    // TODO: update is_captured name to is_captured_from_shopify for better readability
    shopifyMarketData.is_captured = true; // is captured because we are using the market id from shopify
    MarketDataHandler.updateMarketData(shopifyMarketData);
    return;
  }

  // Market ID from shopify is not available, so we need to check if the market data is already set in the session data
  const guessedMarket = MarketDataHandler.findMarketMatch(shopifyMarketData);

  // If no guessed market is found, we can't update the market data
  if (!guessedMarket) return;

  // Update the id
  shopifyMarketData.mid = guessedMarket.mid;
  shopifyMarketData.is_captured = false; // is not captured because we are guessing the market
  shopifyMarketData.mh = guessedMarket.mh;

  // If no region is available, we'll use the region from the guessed market
  if (!shopifyMarketData.re) {
    // Use the first region
    shopifyMarketData.re = guessedMarket.mr[0];
  }

  // If no locale is available, we'll use the locale from the guessed market
  if (!shopifyMarketData.lo) {
    shopifyMarketData.lo = guessedMarket.ml;
  }

  const currentMarketData = MarketDataHandler.getMarketData();

  if (
    currentMarketData.mid &&
    currentMarketData.is_captured &&
    !shopifyMarketData.mid
  ) {
    // Captured market data already set, and the new marekt data doesn't have a market id
    // Quit and don't update the market data
    return;
  }

  // Update the market data
  MarketDataHandler.updateMarketData(shopifyMarketData);
}
